import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { DriveStatusResult, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../../ws/WsProvider.js';

export const DRIVE_STATUS_QUERY_KEY = ['drive-status'] as const;

export interface DriveConnectionStatus extends DriveStatusResult {
  /** Derived in the query's `select` (not render — `Date.now()` during
   * render trips `react-hooks/purity`), so it's a stable read tied to when
   * the status data itself last changed rather than every re-render. */
  isExpired: boolean;
}

export interface UseDriveConnectionResult {
  status: DriveConnectionStatus | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Resolves the server-built consent `authUrl` — caller navigates the
   * browser to it (`drive.ts`'s own doc comment: this reuses the shared
   * OAuth callback with a `purpose: 'drive_connect'` state nonce). */
  connect: () => Promise<string>;
  isConnecting: boolean;
  connectError: string | null;
  disconnect: () => Promise<void>;
  isDisconnecting: boolean;
  disconnectError: string | null;
}

/** F6 connect/status/disconnect (`/settings/drive`). */
export function useDriveConnection(): UseDriveConnectionResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected';

  const query = useQuery<DriveStatusResult, ErrorAlert, DriveConnectionStatus>({
    queryKey: DRIVE_STATUS_QUERY_KEY,
    queryFn: () => requestTyped(wsClient, 'drive.status', undefined),
    select: (data) => ({ ...data, isExpired: data.expiresAt ? data.expiresAt.getTime() < Date.now() : false }),
    enabled,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: DRIVE_STATUS_QUERY_KEY });
  }, [queryClient]);

  const connectMutation = useMutation<string, ErrorAlert>({
    mutationFn: async () => {
      const result = await requestTyped(wsClient, 'drive.connect', undefined);
      return result.authUrl;
    },
  });

  const disconnectMutation = useMutation<void, ErrorAlert>({
    mutationFn: async () => {
      await requestTyped(wsClient, 'drive.disconnect', undefined);
    },
    onSuccess: invalidate,
  });

  return {
    status: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    onRetry: () => void query.refetch(),
    connect: () => connectMutation.mutateAsync(),
    isConnecting: connectMutation.isPending,
    connectError: connectMutation.isError ? connectMutation.error.message : null,
    disconnect: () => disconnectMutation.mutateAsync(),
    isDisconnecting: disconnectMutation.isPending,
    disconnectError: disconnectMutation.isError ? disconnectMutation.error.message : null,
  };
}
